import type { ParatrooperGame, Trooper, Bullet, Plane, Explosion } from "./types.js";

const CANVAS_W = 800;
const CANVAS_H = 600;
const GROUND_Y = 560;
const CANNON_Y = 540;

export function createGame(level: number = 1): ParatrooperGame {
  return {
    score: 0,
    trooperLands: 0,
    maxLands: 8 + level * 2,
    troopers: [],
    bullets: [],
    planes: [],
    explosions: [],
    gameOver: false,
    cannonX: CANVAS_W / 2,
    level,
    spawnTimer: 0,
    spawnInterval: Math.max(800, 3000 - level * 200),
  };
}

export function tick(game: ParatrooperGame, dt: number, shoot: boolean): ParatrooperGame {
  if (game.gameOver) return game;

  const g = { ...game };
  const dtSec = dt / 1000;

  g.spawnTimer += dt;

  if (g.spawnTimer >= g.spawnInterval) {
    g.spawnTimer = 0;
    if (g.planes.length < 2) {
      const isHeli = Math.random() < 0.3;
      const fromLeft = Math.random() < 0.5;
      g.planes.push({
        x: fromLeft ? -80 : CANVAS_W + 80,
        y: isHeli ? 80 + Math.random() * 40 : 30 + Math.random() * 40,
        speed: isHeli
          ? (20 + Math.random() * 20) * (fromLeft ? 1 : -1)
          : (60 + Math.random() * 40) * (fromLeft ? 1 : -1),
        dropTimer: 0,
        alive: true,
        explosionTimer: 0,
        isHeli,
      });
    }
  }

  const newTroopers: Trooper[] = [];
  for (const t of g.troopers) {
    if (!t.alive) continue;
    const nt = { ...t };
    if (!nt.parachute) {
      nt.vy += 400 * dtSec;
      if (nt.vy > 300) nt.vy = 300;
    } else {
      nt.vy += 50 * dtSec;
      if (nt.vy > 80) nt.vy = 80;
    }
    nt.y += nt.vy * dtSec;
    if (nt.y >= GROUND_Y) {
      nt.y = GROUND_Y;
      nt.alive = false;
      if (!nt.parachute) {
        g.explosions.push({ x: nt.x, y: GROUND_Y, timer: 30 });
      } else {
        g.trooperLands++;
      }
    }
    if (nt.alive) newTroopers.push(nt);
  }
  g.troopers = newTroopers;

  if (g.trooperLands >= g.maxLands) {
    g.gameOver = true;
    return g;
  }

  if (shoot) {
    g.bullets.push({ x: g.cannonX, y: CANNON_Y - 10, vy: -400 });
  }

  const newBullets: Bullet[] = [];
  for (const b of g.bullets) {
    const nb = { ...b };
    nb.y += nb.vy * dtSec;
    if (nb.y > 0) newBullets.push(nb);
  }
  g.bullets = newBullets;

  for (const b of g.bullets) {
    for (const t of g.troopers) {
      if (!t.alive) continue;
      const dx = b.x - t.x;
      const dy = b.y - t.y;
      if (Math.sqrt(dx * dx + dy * dy) < 16) {
        t.alive = false;
        b.y = -1;
        g.score += t.parachute ? 50 : 100;
      }
    }
    for (const p of g.planes) {
      if (!p.alive) continue;
      const radius = p.isHeli ? 40 : 36;
      const dx = b.x - p.x;
      const dy = b.y - (p.y + 5);
      if (Math.sqrt(dx * dx + dy * dy) < radius) {
        p.alive = false;
        p.explosionTimer = 60;
        b.y = -1;
        g.score += p.isHeli ? 300 : 200;
        g.explosions.push({ x: p.x, y: p.y + 5, timer: 60 });
      }
    }
  }

  const newPlanes: Plane[] = [];
  for (const p of g.planes) {
    const np = { ...p };

    if (!np.alive) {
      np.explosionTimer -= 1;
      if (np.explosionTimer > 0) newPlanes.push(np);
      continue;
    }

    if (np.isHeli) {
      const targetX = CANVAS_W / 2 + Math.sin(Date.now() * 0.0015) * 120;
      const dx = targetX - np.x;
      np.x += Math.sign(dx) * Math.min(Math.abs(dx) * 0.015, 0.8);
    } else {
      np.x += np.speed * dtSec;
    }

    np.dropTimer += dt;

    if (np.isHeli) {
      if (np.dropTimer > 1200 && np.x > 80 && np.x < CANVAS_W - 80) {
        np.dropTimer = 0;
        g.troopers.push({
          x: np.x + (Math.random() - 0.5) * 20,
          y: np.y + 20,
          vy: 0,
          parachute: false,
          alive: true,
        });
      }
    } else {
      if (np.dropTimer > 1500) {
        np.dropTimer = 0;
        if (np.x > 50 && np.x < CANVAS_W - 50) {
          g.troopers.push({
            x: np.x, y: np.y + 20,
            vy: 0, parachute: true, alive: true,
          });
        }
      }
    }

    if (!np.isHeli && (np.x > CANVAS_W + 100 || np.x < -100)) {
      continue;
    }
    newPlanes.push(np);
  }
  g.planes = newPlanes;

  const newExplosions: Explosion[] = [];
  for (const e of g.explosions) {
    e.timer -= 1;
    if (e.timer > 0) newExplosions.push(e);
  }
  g.explosions = newExplosions;

  return g;
}

export function setCannonX(game: ParatrooperGame, x: number): ParatrooperGame {
  return { ...game, cannonX: Math.max(30, Math.min(CANVAS_W - 30, x)) };
}

export function getCanvasSize() {
  return { w: CANVAS_W, h: CANVAS_H, groundY: GROUND_Y, cannonY: CANNON_Y };
}
